import { useState, useEffect } from "react";
import { T, UI } from "../constants/tokens";
import { supabase } from "../lib/supabase";
import { PROFILE_CONTENT, PROFILE_DISCLAIMER } from "../data/profileContent";
import { dateNumeric } from "../utils/formatDate";
import { TOTAL as QUESTIONNAIRE_TOTAL } from "./OnboardingFlow";
import Icon from "./Icon";
import { modalScrim, modalPanel } from "./Modal";
import { Skeleton, Loading } from "./Skeleton";

// Three identities, not a scale and not a verdict: green for "balanced"
// against purple for "aggressive" would read as approval of one of them, and
// a grey ramp would imply one is more of something than another. Distinct
// hues from the categorical series say only "a different one".
const PROFILE_COLOR = {
  conservative: UI.categorical[0],
  balanced:     UI.categorical[1],
  aggressive:   UI.categorical[3],
};

// The icon already separates the three — check, warning, cycle. Green for a
// strength and amber for a caution would add a good/bad reading the text
// itself does not make.
const KIND_META = {
  strength: { icon: "checkCircle", color: UI.text.secondary, bg: UI.line.hairline },
  caution:  { icon: "warning",     color: UI.text.secondary, bg: UI.line.hairline },
  habit:    { icon: "refresh",     color: UI.text.secondary, bg: UI.line.hairline },
};

const formatDate = (iso, lang) => dateNumeric(iso, { lang });

// ─── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ lang, onStartQuestionnaire }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 24px", textAlign: "center", gap: 16 }}>
      <Icon name="compass" size={54} color={UI.text.muted}/>
      <div style={{ fontSize: 20, fontWeight: 800, color: UI.text.primary }}>
        {lang === "en" ? "No profile yet" : "עוד לא מילאת את אבחון הפרופיל"}
      </div>
      <div style={{ fontSize: 14, color: UI.text.muted, maxWidth: 320, lineHeight: 1.7 }}>
        {lang === "en"
          ? `Answer ${QUESTIONNAIRE_TOTAL} short questions to get your investor profile.`
          : `${QUESTIONNAIRE_TOTAL} שאלות קצרות שיעזרו לנו להתאים את החוויה לצרכים שלך.`}
      </div>
      <button onClick={onStartQuestionnaire} className="si-btn-gold" style={{
        marginTop: 8, background: "transparent", border: `1px solid ${UI.accent.base}`, borderRadius: 8,
        padding: "14px 30px", fontSize: 15, fontWeight: 700, color: UI.accent.base, cursor: "pointer",
      }}>
        {lang === "en" ? "Start diagnosis →" : "מלא את האבחון ←"}
      </button>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function MyProfileTab({ lang = "he", onResetProfile }) {
  const [loading,        setLoading]        = useState(true);
  const [profileRow,     setProfileRow]     = useState(null);
  const [confirmReset,   setConfirmReset]   = useState(false);

  useEffect(() => {
    async function load() {
      if (!supabase) { setLoading(false); return; }
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) { setLoading(false); return; }
        const { data } = await supabase
          .from("investor_profiles")
          .select("profile_type, completed_at")
          .eq("user_id", session.user.id)
          .maybeSingle();
        setProfileRow(data ?? null);
      } catch (e) {
        console.error("[MyProfileTab] load error:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleConfirmReset = () => {
    setConfirmReset(false);
    onResetProfile?.();
  };

  // Loading — skeleton mirrors the two profile cards instead of a spinner
  if (loading) return (
    <Loading style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 680, margin: "0 auto" }}>
      {[112, 180].map((h, i) => (
        <div key={i} style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "22px 22px" }}>
          <Skeleton w="38%" h={14} mb={14}/>
          <Skeleton h={h - 60}/>
        </div>
      ))}
    </Loading>
  );

  const content = profileRow ? PROFILE_CONTENT[profileRow.profile_type] : null;

  // No profile
  if (!content) return <EmptyState lang={lang} onStartQuestionnaire={onResetProfile}/>;

  // An unrecognised type gets a plain neutral — not a guess at a category.
  const color = PROFILE_COLOR[profileRow.profile_type] ?? UI.text.muted;
  const isEn  = lang === "en";

  return (
    <div dir={isEn ? "ltr" : "rtl"} style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 680, margin: "0 auto" }}>

      {/* ── Header card ── */}
      <div style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "22px 22px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: UI.text.muted, textTransform: "uppercase", marginBottom: 12 }}>
          {isEn ? "Your Investor Profile" : "פרופיל המשקיע שלך"}
        </div>
        {/* Badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
          <span style={{
            display: "flex", lineHeight: 1,
            background: `${color}18`, borderRadius: 12,
            padding: "10px 14px", border: `1.5px solid ${color}40`,
          }}><Icon name={content.icon} size={52} color={color}/></span>
          <div>
            <div style={{ fontSize: 26, fontWeight: 800, color, letterSpacing: "-.5px" }}>{content.label}</div>
            <div style={{ fontSize: 12, color: UI.text.muted, marginTop: 4 }}>
              {isEn ? "Diagnosed on" : "אובחן ב-"}{formatDate(profileRow.completed_at, lang)}
            </div>
          </div>
        </div>
        {/* Summary paragraphs */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {content.summary.map((p, i) => (
            <p key={i} style={{ margin: 0, fontSize: 14, color: UI.text.secondary, lineHeight: 1.75 }}>{p}</p>
          ))}
        </div>
      </div>

      {/* ── Insights ── */}
      <div style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "18px 22px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: UI.text.muted, textTransform: "uppercase", marginBottom: 14 }}>
          {isEn ? "About your style" : "מאפייני הסגנון שלך"}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {content.insights.map((ins, i) => {
            const km = KIND_META[ins.kind] ?? KIND_META.habit;
            return (
              <div key={i} style={{
                background: km.bg, border: `1px solid ${UI.line.strong}`,
                borderRadius: 12, padding: "12px 14px",
                display: "flex", gap: 12, alignItems: "flex-start",
              }}>
                <span style={{ display: "flex", lineHeight: 1.2, flexShrink: 0, marginTop: 1 }}><Icon name={km.icon} size={18} color={km.color}/></span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: km.color, marginBottom: 3 }}>{ins.title}</div>
                  <div style={{ fontSize: 13, color: UI.text.secondary, lineHeight: 1.65 }}>{ins.text}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Reset button ── */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <button onClick={() => setConfirmReset(true)} style={{
          background: "transparent", border: `1px solid ${UI.line.hairline}`,
          borderRadius: 8, padding: "10px 24px", fontSize: 13, fontWeight: 700,
          color: UI.text.muted, cursor: "pointer",
        }}>
          {isEn ? "Retake questionnaire" : "מלא שאלון מחדש"}
        </button>
      </div>

      {/* ── Disclaimer ── */}
      <div style={{ fontSize: 11, color: UI.text.muted, lineHeight: 1.7, padding: "0 4px", textAlign: "center" }}>
        {PROFILE_DISCLAIMER}
      </div>

      {/* ── Confirm reset dialog ── */}
      {confirmReset && (
        <div style={modalScrim(500)}>
          <div dir={isEn ? "ltr" : "rtl"} style={{ ...modalPanel({ width: 360 }), textAlign: "center" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: UI.text.primary, marginBottom: 10 }}>
              {isEn ? "Retake questionnaire?" : "מלא שאלון מחדש?"}
            </div>
            <div style={{ fontSize: 13, color: UI.text.muted, marginBottom: 24, lineHeight: 1.65 }}>
              {isEn ? "Your current profile will be replaced." : "האבחון הנוכחי יוחלף, להמשיך?"}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={() => setConfirmReset(false)} style={{
                flex: 1, padding: "10px 0", background: "transparent",
                border: `1px solid ${UI.line.hairline}`, borderRadius: 8,
                fontSize: 14, fontWeight: 700, color: UI.text.muted, cursor: "pointer",
              }}>
                {isEn ? "Cancel" : "ביטול"}
              </button>
              <button onClick={handleConfirmReset} className="si-btn-gold" style={{
                flex: 1, padding: "10px 0", background: "transparent",
                border: `1px solid ${UI.accent.base}`, borderRadius: 8,
                fontSize: 14, fontWeight: 700, color: UI.accent.base, cursor: "pointer",
              }}>
                {isEn ? "Continue" : "המשך"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
